import { memo } from 'react'

import {
  ActionIcon,
  Box,
  Group,
  Select,
  type SelectProps,
  Stack,
  Text,
} from '@mantine/core'

import { IconArrowDown, IconArrowsDownUp } from '@repo/common/components/Icons'
import { TokenRender } from '@repo/common/components/TokenRender'
import {
  countFormatter,
  getMaxFractionDigits,
  usdFormatter,
} from '@repo/common/helpers/formatters'
import { type InSideType, useSwap } from '@repo/common/queries/fission_dex'
import {
  type FundSymbol,
  swapable_symbols,
  type TokenSymbol,
} from '@repo/common/queries/products'

import { ContentBox } from '@/components/ContentBox'
import {
  type BasePanelProps,
  FlowButton,
  normalize_progress,
  NumberRow,
  RatioAndDisclosures,
  type StepsType,
  useFlowControl,
} from '@/flows/flow_helpers'
import {
  type ExtraAuthSyncingProps,
  PanelAuthSyncing,
  useSkipAuthSyncing,
} from '@/panels/PanelAuthSyncing'
import {
  PanelCheckWalletRisk,
  useSkipWalletRisk,
} from '@/panels/PanelCheckWalletRisk'
import {
  PanelConfirmEmail,
  useSkipConfirmEmail,
} from '@/panels/PanelConfirmEmail'
import {
  PanelLocationAttestation,
  useSkipLocationVerify,
} from '@/panels/PanelLocationAttestation'
import {
  type ExtraPanelPermitProps,
  PanelPermit2,
  useSkipPermit2,
} from '@/panels/PanelPermit2'
import {
  PanelRegisterWallet,
  useRegisterWalletTitle,
  useSkipRegisterWallet,
} from '@/panels/PanelRegisterWallet'
import { PanelSignup, useSkipLogin } from '@/panels/PanelSignup'

import { useSwapFields } from './useSwapFields'

import classes from './Swap.module.css'

// export function is_valid_symbol(symbol: string): FundSymbol | null {
//   if (fund_symbols.includes(symbol.toUpperCase() as FundSymbol)) {
//     return symbol.toUpperCase() as FundSymbol
//   }
//   return null
// }

type SwapPanelProps = BasePanelProps &
  ExtraAuthSyncingProps &
  ExtraPanelPermitProps

export const allowed_kyc_levels: ExtraAuthSyncingProps['allowed_kyc_levels'] = [
  'location',
  'sdd',
  'full',
]

const steps: StepsType<SwapPanelProps> = normalize_progress([
  {
    title: 'Signup',
    Panel: PanelSignup,
    progress: 10,
    useSkipCheck: useSkipLogin,
  },
  {
    title: 'Confirm Email',
    Panel: PanelConfirmEmail,
    progress: 10,
    useSkipCheck: useSkipConfirmEmail,
  },
  {
    title: useRegisterWalletTitle,
    Panel: PanelRegisterWallet,
    progress: 5,
    useSkipCheck: useSkipRegisterWallet,
  },
  {
    title: 'Location Verification',
    Panel: PanelLocationAttestation,
    progress: 5,
    useSkipCheck: useSkipLocationVerify,
  },
  {
    title: 'Wallet Check',
    Panel: PanelCheckWalletRisk,
    progress: 5,
    useSkipCheck: useSkipWalletRisk,
  },
  {
    title: 'Enable Permit2',
    Panel: PanelPermit2,
    progress: 5,
    useSkipCheck: useSkipPermit2,
  },
  {
    title: 'Auth Syncing',
    Panel: PanelAuthSyncing,
    progress: 5,
    useSkipCheck: useSkipAuthSyncing,
  },
])

export const Swap = memo<{
  fund_symbol: FundSymbol
  in_side: InSideType
  onCompleted: () => void
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function Swap({ fund_symbol, in_side, onCompleted }) {
  const {
    form,
    balance_in,
    balance_out,
    wallet_isConnected,
    form_valid,
    paused,
    error_reason,
    tokens_in_focus,
    tokens_out_focus,
    available_fund_tokens,
    available_usdc_tokens,
    is_loading,
    swap_data,
  } = useSwapFields({
    fund_symbol,
    in_side,
  })

  const swap_query = useSwap(form.getValues())

  const { submitted, onSubmit, modal } = useFlowControl({
    steps,
    extra_args: {
      symbol_in: form.getValues().symbol_in,
      allowed_kyc_levels,
    },
    final_action: () =>
      swap_query
        .mutateAsync({ tokens: form.getValues().tokens_in })
        .then(onCompleted),
  })

  return (
    <Stack maw="100%">
      {modal}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack
          maw="calc(100vw - 2 * var(--app-body-margin))"
          w="500px"
          gap="xxs"
        >
          <Box pos="relative">
            <ActionIcon
              className={classes.switch_button}
              variant="filled"
              aria-label="switch direction"
              disabled={submitted}
              onClick={() =>
                form.setFieldValue('symbol_in', form.getValues().symbol_out)
              }
            >
              <IconArrowsDownUp
                style={{ width: '70%', height: '70%' }}
                stroke={1.5}
              />
            </ActionIcon>
            <ContentBox
              p="lg"
              gap="xs"
              ref={tokens_in_focus.ref}
              mod={{
                focused: tokens_in_focus.focused,
                error: Boolean(form.getInputProps('tokens_in').error),
              }}
              data-testid="sell_container"
            >
              <Text size="md" lh="1" opacity={0.7}>
                Sell
              </Text>
              <NumberRow
                {...form.getInputProps('tokens_in', { withError: false })}
                disabled={is_loading || submitted}
                aria-label="sell tokens"
                decimalScale={getMaxFractionDigits(form.getValues().symbol_in)}
                balance_number={balance_in}
                balance_maximumFractionDigits={getMaxFractionDigits(
                  form.getValues().symbol_in,
                )}
                max_button={() => form.setFieldValue('tokens_in', balance_in)}
                symbol={
                  <Select
                    {...form.getInputProps('symbol_in')}
                    aria-label="sell token"
                    w="125px"
                    size="md"
                    disabled={submitted}
                    data={swapable_symbols}
                    classNames={{
                      input: classes.select,
                      dropdown: classes.select_dropdown,
                    }}
                    allowDeselect={false}
                    renderOption={renderSelectOption}
                    rightSection={<IconArrowDown size="16px" />}
                    leftSection={
                      <TokenRender
                        size="var(--mantine-font-size-xl)"
                        symbol={form.getValues().symbol_in}
                      />
                    }
                  />
                }
              />
            </ContentBox>
          </Box>
          <ContentBox
            p="lg"
            gap="xs"
            ref={tokens_out_focus.ref}
            mod={{
              focused: tokens_out_focus.focused,
              error: Boolean(form.getInputProps('tokens_out').error),
            }}
            data-testid="buy_container"
          >
            <Text size="md" lh="1" opacity={0.7}>
              Buy
            </Text>
            <NumberRow
              {...form.getInputProps('tokens_out', { withError: false })}
              disabled={is_loading || submitted}
              aria-label="buy tokens"
              decimalScale={getMaxFractionDigits(form.getValues().symbol_out)}
              balance_number={balance_out}
              balance_maximumFractionDigits={getMaxFractionDigits(
                form.getValues().symbol_out,
              )}
              symbol={
                <Select
                  {...form.getInputProps('symbol_out')}
                  aria-label="buy token"
                  w="125px"
                  size="md"
                  disabled={submitted}
                  data={swapable_symbols}
                  classNames={{
                    input: classes.select,
                    dropdown: classes.select_dropdown,
                  }}
                  allowDeselect={false}
                  renderOption={renderSelectOption}
                  rightSection={<IconArrowDown size="16px" />}
                  leftSection={
                    <TokenRender
                      size="var(--mantine-font-size-xl)"
                      symbol={form.getValues().symbol_out}
                    />
                  }
                />
              }
            />
          </ContentBox>
          <FlowButton
            wallet_isConnected={wallet_isConnected}
            form_valid={form_valid}
            is_loading={is_loading}
            paused={paused}
            error_reason={error_reason}
            mutation_query={swap_query}
            action_word="Swap"
          />
          <RatioAndDisclosures
            symbol={fund_symbol}
            price={swap_data?.usdc_price}
            maximumFractionDigits={2}
          >
            <Stack gap="xs" px="sm">
              <Group justify="space-between">
                <Text size="sm" opacity={0.7}>
                  Max. slippage
                </Text>
                <Text size="sm">5%</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" opacity={0.7}>
                  Swap Fee (1%)
                </Text>
                <Text size="sm">
                  {usdFormatter(swap_data?.system_fee_usdc)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" opacity={0.7}>
                  Available USDC tokens
                </Text>
                <Text size="sm">{countFormatter(available_usdc_tokens)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" opacity={0.7}>
                  Available {fund_symbol} tokens
                </Text>
                <Text size="sm">{countFormatter(available_fund_tokens)}</Text>
              </Group>
            </Stack>
          </RatioAndDisclosures>
        </Stack>
      </form>
    </Stack>
  )
})

export const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
  <Group flex="1" gap="xxs">
    <TokenRender
      size="var(--mantine-font-size-xl)"
      symbol={option.value as TokenSymbol}
    />
    {option.label}
  </Group>
)
